import type { ObjectTag } from '@atlas/types'
import { AxisCode } from '@atlas/types'
import { ListSkeleton } from '@atlas/ui-components'
import { Tab, TabList, TabPanel, Tabs } from '@atlas/ui-primitives'
import { useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { MergeValueAction } from '@/components/merge/MergeValueAction'
import { useBrandLogos, useBrandPage } from '@/hooks/useExplore'
import { useThumbnails } from '@/hooks/useThumbnails'
import { isNotFound } from '@/lib/read-error'
import { BrandBreadcrumb } from './BrandBreadcrumb'
import { BrandCopyTab } from './BrandCopyTab'
import { BrandDetailsTab } from './BrandDetailsTab'
import { BrandEditTab } from './BrandEditTab'
import { BrandHero } from './BrandHero'
import { BrandIdentityTab } from './BrandIdentityTab'
import { BrandProductsCampaignsTab } from './BrandProductsCampaignsTab'
import { BrandRulesTab } from './BrandRulesTab'
import { linksOf, taglineOf } from './brand-meta'
import { CatalogAdminTabs, useCatalogAdminTabs } from './CatalogAdminTabs'
import { MetaTab } from './MetaTab'
import { ALL_AUDIENCES } from './ProductIndex'

/**
 * Axes whose labels feed the hero's `Type:` chip. `product_type` and `product_class`
 * carried this before the Veeva realignment removed them; the product family the brand
 * sits above is what names a brand's kind now, and it is read from the hierarchy rather
 * than from an axis of its own.
 */
const TYPE_AXES: string[] = []

/**
 * The tabs, in reading order: what the brand LOOKS like, what it IS, what it SAYS, what
 * constrains it, and what it is sold as. Identity opens the page because the brand's assets are
 * what a reader arrives for.
 */
const IDENTITY_TAB = 'identity'
const DETAILS_TAB = 'brand-details'
const COPY_TAB = 'copy'
const RULES_TAB = 'rules'
const PRODUCTS_TAB = 'products-campaigns'
const EDIT_TAB = 'edit'
const META_TAB = 'meta'

const labelsOn = (tags: ObjectTag[], axisCodes: string[]): string[] => [
	...new Set(tags.filter((t) => axisCodes.includes(t.axisCode)).map((t) => t.label)),
]

/**
 * The Brand page — one brand in one market context.
 *
 * The hero and the brand's own prose sit above five tabs, each of which owns its reads. An
 * inactive tab panel is NOT mounted, so a tab costs nothing until it is selected: arriving on
 * the page reads the brand and its assets, and nothing else.
 *
 * The Identity tab narrows its assets to the market when one is resolved, by COVERAGE: a file
 * tagged `emea` applies in Spain, one tagged `emea_fr` does not. The server walks the tree, so the
 * market code alone is the whole filter.
 *
 * The other tabs are still read for the brand alone. The rules are the exception in the opposite
 * direction — the server resolves their applicability itself.
 */
/**
 * The brand page's tab strip. Reading tabs first, then a gap, then the two that ACT on the
 * brand — the separation is the layout's, so a reader does not reach Edit while scanning
 * for content. Those two are administrator-only; see `CatalogAdminTabs`.
 */
function BrandTabStrip({ canEdit }: { canEdit: boolean }) {
	return (
		<TabList variant="underline" className="mb-4 flex w-full" aria-label="Brand sections">
			<Tab id={IDENTITY_TAB}>Identity</Tab>
			<Tab id={DETAILS_TAB}>Brand details</Tab>
			<Tab id={COPY_TAB}>Copy</Tab>
			<Tab id={RULES_TAB}>Rules</Tab>
			<Tab id={PRODUCTS_TAB}>Products &amp; campaigns</Tab>
			<CatalogAdminTabs editTabId={EDIT_TAB} metaTabId={META_TAB} canEdit={canEdit} subject="brand" />
		</TabList>
	)
}

export function BrandPage() {
	const { brandId } = useParams<{ brandId: string }>()
	const navigate = useNavigate()
	const [searchParams, setSearchParams] = useSearchParams()
	const market = searchParams.get('market')

	// The tabs' own picks live here, not in the tab components: an inactive panel is not
	// mounted, so state held inside one is discarded the moment the reader looks at another
	// tab. Above the early returns because hooks cannot be conditional.
	const [productQuery, setProductQuery] = useState('')
	const [audience, setAudience] = useState<string>(ALL_AUDIENCES)
	const [copyLocale, setCopyLocale] = useState<string | null>(null)
	const tabs = useCatalogAdminTabs(IDENTITY_TAB, [EDIT_TAB, META_TAB], 'brand')

	const { data: page, isLoading, isError, error } = useBrandPage(brandId ?? null, market)

	// The hero's logo, resolved from the brand's own assets rather than a column. Keyed by the
	// brand CODE, which the page only has once `page` has loaded — so this read starts a beat
	// after the page read rather than in parallel with it. Above the early returns because hooks
	// cannot be conditional; disabled until the code exists.
	const { data: logos = [] } = useBrandLogos(page?.brand.code ?? null)
	// The first logo: several means regional wordmarks, and this page holds no ladder to choose
	// between them by market (see `useBrandLogos`).
	const logoFile = logos[0]
	const logoThumbnails = useThumbnails(logoFile ? [logoFile] : [])

	if (isLoading) return <ListSkeleton label="Loading…" />
	// An absent brand is a dead end to state plainly; any other failure is worth a retry.
	if (isNotFound(error)) return <p className="text-ds-text-muted">Brand not found.</p>
	if (isError) return <p className="text-ds-text-muted">Could not load this brand. Please retry.</p>
	if (!page) return <p className="text-ds-text-muted">Brand not found.</p>

	const marketLabel = page.market?.label ?? null
	const marketCode = page.market?.code ?? null

	const clearMarket = () => {
		searchParams.delete('market')
		setSearchParams(searchParams, { replace: true })
	}

	return (
		<div>
			{/* `resolvedName`, not `brand.name`: on a market-scoped page the brand may go by
			    another name there (Banamine in the US is Finadyne in the EU), and the crumb has
			    to agree with the title below it. Falls back to `brand.name` server-side. */}
			<BrandBreadcrumb marketLabel={marketLabel} brandName={page.resolvedName} />

			<BrandHero
				name={page.resolvedName}
				businessUnit={page.brand.businessUnit}
				species={labelsOn(page.brandTags, [AxisCode.SPECIES])}
				types={labelsOn(page.brandTags, TYPE_AXES)}
				marketLabel={marketLabel}
				onClearMarket={clearMarket}
				logoUrl={logoFile ? (logoThumbnails[logoFile.fileId] ?? null) : null}
				tagline={taglineOf(page.brand.meta)}
			/>

			{/* Real prose when the brand has it; nothing at all when it does not. */}
			{page.brand.description ? <p className="mb-6 max-w-2xl text-ds-text-muted">{page.brand.description}</p> : null}

			<Tabs selectedKey={tabs.selectedKey} onSelectionChange={tabs.onSelectionChange}>
				<BrandTabStrip canEdit={tabs.canEdit} />

				<TabPanel id={IDENTITY_TAB}>
					<BrandIdentityTab
						brandCode={page.brand.code}
						links={linksOf(page.brand.meta)}
						names={page.names}
						marketCode={marketCode}
						// `brand.name`, NOT `resolvedName`: the naming card's baseline row is the name in
						// force wherever no override applies. `resolvedName` is already this market's
						// answer, so using it would make the baseline restate the page title.
						brandName={page.brand.name}
					/>
				</TabPanel>

				<TabPanel id={DETAILS_TAB}>
					<BrandDetailsTab brandCode={page.brand.code} brandName={page.brand.name} />
				</TabPanel>

				<TabPanel id={COPY_TAB}>
					<BrandCopyTab
						brandCode={page.brand.code}
						productCodes={page.products.map((p) => p.code)}
						locale={copyLocale}
						onLocaleChange={setCopyLocale}
					/>
				</TabPanel>

				<TabPanel id={RULES_TAB}>
					<BrandRulesTab brandCode={page.brand.code} marketCode={marketCode} />
				</TabPanel>

				<TabPanel id={PRODUCTS_TAB}>
					<BrandProductsCampaignsTab
						page={page}
						query={productQuery}
						onQueryChange={setProductQuery}
						audience={audience}
						onAudienceChange={setAudience}
					/>
				</TabPanel>

				<TabPanel id={EDIT_TAB}>
					<BrandEditTab page={page} market={marketCode} />
				</TabPanel>

				<TabPanel id={META_TAB}>
					<MetaTab
						subject="brand"
						code={page.brand.code}
						valueId={page.brand.valueId}
						meta={page.brand.meta}
						valueMeta={page.brandValueMeta}
						mappings={page.namesByMarket}
						identityAction={
							<MergeValueAction
								valueId={page.brand.valueId}
								code={page.brand.code}
								label={page.brand.name}
								// The merged brand's page stops resolving, so the reader is returned to
								// Explore rather than left on one that will fail to reload.
								onMerged={() => void navigate('/explore')}
							/>
						}
					/>
				</TabPanel>
			</Tabs>
		</div>
	)
}
